/**
 * Onglet WhatsApp dans Agent Settings
 * Permet d'activer/configurer WhatsApp pour un agent
 */
import { useState, useEffect } from 'react'
import { getApiBase, getToken, getWhatsAppMetaWebhookCallbackUrl } from '../lib/utils'
import WhatsAppQRModal from './WhatsAppQRModal'
import ResponseDelayEditor from './ResponseDelayEditor'
import BusinessHoursEditor from './BusinessHoursEditor'
import WhatsAppMessagesConfig from './WhatsAppMessagesConfig'

interface WhatsAppConnection {
  id: number
  agent_id: number
  user_id: number
  status: 'disconnected' | 'connecting' | 'connected' | 'error'
  phone_number: string | null
  display_name: string | null
  qr_code: string | null
  enabled: boolean
  connection_type: 'baileys' | 'meta_api'
  meta_access_token: string | null
  meta_phone_number_id: string | null
  meta_webhook_verify_token: string | null
  timezone: string
  response_delay_seconds: number
  business_hours: any
  welcome_message_enabled: boolean
  welcome_message: string | null
  closed_message_enabled: boolean
  closed_message: string | null
  human_takeover_enabled: boolean
  last_connected_at: string | null
  last_disconnected_at: string | null
  disconnect_reason: string | null
  created_at: string
  updated_at: string
}

interface WhatsAppTabProps {
  agentId: number
}

// Types pour les horaires
interface DaySchedule {
  enabled: boolean
  open: string
  close: string
}

interface BusinessHours {
  monday: DaySchedule
  tuesday: DaySchedule
  wednesday: DaySchedule
  thursday: DaySchedule
  friday: DaySchedule
  saturday: DaySchedule
  sunday: DaySchedule
}

const DEFAULT_DAY_SCHEDULE: DaySchedule = { enabled: false, open: '09:00', close: '18:00' }

/** Doit correspondre au service Node (`META_WEBHOOK_VERIFY_TOKEN`) ou à cette valeur par défaut. */
const DEFAULT_META_WEBHOOK_VERIFY = 'totemium_webhook_2026'

const META_WEBHOOK_DOCS_URL =
  'https://developers.facebook.com/docs/whatsapp/cloud-api/guides/set-up-webhooks'

// Transformer les horaires backend (start/end) vers frontend (open/close)
const transformBusinessHoursFromBackend = (backendHours: any): BusinessHours | null => {
  if (!backendHours) return null
  
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
  const frontendHours: BusinessHours = {
    monday: DEFAULT_DAY_SCHEDULE,
    tuesday: DEFAULT_DAY_SCHEDULE,
    wednesday: DEFAULT_DAY_SCHEDULE,
    thursday: DEFAULT_DAY_SCHEDULE,
    friday: DEFAULT_DAY_SCHEDULE,
    saturday: DEFAULT_DAY_SCHEDULE,
    sunday: DEFAULT_DAY_SCHEDULE,
  }
  
  for (const day of days) {
    const schedule = backendHours[day]
    if (schedule) {
      frontendHours[day] = {
        enabled: schedule.enabled ?? false,
        open: schedule.start ?? '09:00',
        close: schedule.end ?? '18:00'
      }
    }
  }
  return frontendHours
}

export default function WhatsAppTab({ agentId }: WhatsAppTabProps) {
  const [connection, setConnection] = useState<WhatsAppConnection | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [showQR, setShowQR] = useState(false)
  const [justConnected, setJustConnected] = useState(false)
  const [connectionType, setConnectionType] = useState<'baileys' | 'meta_api'>('baileys')
  const [metaAccessToken, setMetaAccessToken] = useState('')
  const [metaPhoneNumberId, setMetaPhoneNumberId] = useState('')
  const [metaWebhookVerifyToken, setMetaWebhookVerifyToken] = useState(DEFAULT_META_WEBHOOK_VERIFY)
  const [copyHint, setCopyHint] = useState<string | null>(null)
  const [qrFetchError, setQrFetchError] = useState<string | null>(null)
  const [metaTestLoading, setMetaTestLoading] = useState(false)
  const [metaTestResult, setMetaTestResult] = useState<{ success: boolean; message: string } | null>(null)

  const copyWebhookUrl = async () => {
    const url = getWhatsAppMetaWebhookCallbackUrl()
    try {
      await navigator.clipboard.writeText(url)
      setCopyHint('URL copiée')
      window.setTimeout(() => setCopyHint(null), 2500)
    } catch {
      setCopyHint('Copie impossible (navigateur)')
      window.setTimeout(() => setCopyHint(null), 2500)
    }
  }

  // Charger la connexion WhatsApp si elle existe
  useEffect(() => {
    loadConnection()
  }, [agentId])

  // Polling uniquement si status = connecting
  useEffect(() => {
    if (connection?.status !== 'connecting') {
      return
    }

    const interval = setInterval(async () => {
      try {
        const token = getToken()
        const response = await fetch(`${getApiBase()}/whatsapp/connections?agent_id=${agentId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.ok) {
          const data = await response.json()
          if (data.items && data.items.length > 0) {
            const newConnection = data.items[0]
            
            // Détecter l'apparition du QR code et l'afficher automatiquement
            if (newConnection.status === 'connecting' && newConnection.qr_code && !connection.qr_code) {
              setShowQR(true)
            }
            
            // Détecter la connexion réussie
            if (connection.status === 'connecting' && newConnection.status === 'connected') {
              setJustConnected(true)
              setShowQR(false) // Fermer la modal QR automatiquement
              setTimeout(() => setJustConnected(false), 5000) // Notif pendant 5s
            }
            
            setConnection(newConnection)
          }
        }
      } catch (err) {
        console.error('Polling error:', err)
      }
    }, 3000)
    
    return () => clearInterval(interval)
  }, [agentId, connection?.status])

  // Rafraîchir le QR en base tant que la modale est ouverte (Baileys sans qr_code encore)
  useEffect(() => {
    if (!showQR || !connection?.id) return
    if (connection.connection_type !== 'baileys') return
    if (connection.qr_code) {
      setQrFetchError(null)
      return
    }

    let cancelled = false
    let attempts = 0
    const maxAttempts = 35
    const pollMs = 2000
    const connId = connection.id
    let intervalId = 0

    const tick = async () => {
      if (cancelled) return
      attempts += 1
      try {
        const token = getToken()
        const r = await fetch(`${getApiBase()}/whatsapp/connections/${connId}/qr`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (r.ok) {
          const d = await r.json()
          if (d.qr_code) {
            setConnection((prev) =>
              prev && prev.id === connId
                ? { ...prev, qr_code: d.qr_code, status: (d.status as WhatsAppConnection['status']) || prev.status }
                : prev
            )
            setQrFetchError(null)
            if (intervalId) window.clearInterval(intervalId)
            return
          }
        }
      } catch {
        /* ignore */
      }
      if (attempts >= maxAttempts && !cancelled) {
        setQrFetchError(
          "Aucun QR code reçu dans le délai imparti. Vérifiez que le service WhatsApp Node est démarré sur le serveur, ou supprimez l'intégration puis recommencez."
        )
        if (intervalId) window.clearInterval(intervalId)
      }
    }

    intervalId = window.setInterval(tick, pollMs)
    void tick()

    return () => {
      cancelled = true
      if (intervalId) window.clearInterval(intervalId)
    }
  }, [showQR, connection?.id, connection?.connection_type, connection?.qr_code])

  const loadConnection = async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true
    try {
      if (!silent) setLoading(true)
      if (!silent) setError(null)
      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/connections?agent_id=${agentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!response.ok) {
        throw new Error('Erreur lors du chargement')
      }

      const data = await response.json()
      // La connexion existe si items contient au moins un élément
      if (data.items && data.items.length > 0) {
        setConnection(data.items[0])
        
        // Si status = connecting, afficher le QR
        if (data.items[0].status === 'connecting' && data.items[0].qr_code) {
          setShowQR(true)
        }
      }
    } catch (err: any) {
      console.error('Error loading WhatsApp connection:', err)
      // Pas d'erreur si la connexion n'existe pas
      setConnection(null)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  const createConnection = async (
    connType?: 'baileys' | 'meta_api',
    metaConfig?: { metaAccessToken: string; metaPhoneNumberId: string; metaWebhookVerifyToken?: string }
  ) => {
    try {
      setCreating(true)
      setError(null)
      const token = getToken()
      
      const body: any = {
        agent_id: agentId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris',
        response_delay_seconds: 10,
        connection_type: connType || 'baileys'
      }
      
      if (connType === 'meta_api' && metaConfig) {
        body.meta_access_token = metaConfig.metaAccessToken
        body.meta_phone_number_id = metaConfig.metaPhoneNumberId
        body.meta_webhook_verify_token =
          metaConfig.metaWebhookVerifyToken?.trim() || DEFAULT_META_WEBHOOK_VERIFY
      }
      
      const response = await fetch(`${getApiBase()}/whatsapp/connections`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || 'Erreur lors de la création')
      }

      const data = await response.json()
      setConnection(data)

      // Baileys : ouvrir tout de suite la modale de scan (le backend renvoie "connecting")
      // pour éviter l'étape « Reconnecter » puis « Génération du QR » sans modale.
      if (data.connection_type === 'baileys' && data.status === 'connecting') {
        setQrFetchError(null)
        setShowQR(true)
        ;[400, 1200, 3000, 6000].forEach((ms) =>
          window.setTimeout(() => void loadConnection({ silent: true }), ms)
        )
      } else if (data.status === 'connecting' && data.qr_code) {
        window.setTimeout(() => setShowQR(true), 400)
      } else if (data.status === 'connecting') {
        window.setTimeout(() => void loadConnection({ silent: true }), 1000)
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setCreating(false)
    }
  }

  /** Supprime la ligne WhatsApp côté Totemium pour pouvoir choisir Baileys ou Meta à nouveau. */
  const removeWhatsAppIntegration = async () => {
    if (!connection) return

    const detail =
      connection.status === 'connected'
        ? 'La session sera fermée sur le serveur. Vous pourrez ensuite choisir Baileys (QR) ou Meta API.'
        : 'Vous pourrez choisir à nouveau entre Baileys (QR) et Meta API.'

    if (!confirm(`Supprimer toute l'intégration WhatsApp pour cet agent ?\n\n${detail}`)) {
      return
    }

    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/connections/${connection.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        if (response.status === 404) {
          setConnection(null)
          setShowQR(false)
          setQrFetchError(null)
          return
        }
        throw new Error('Erreur lors de la suppression')
      }

      setConnection(null)
      setShowQR(false)
      setQrFetchError(null)
    } catch (err: any) {
      setError(err.message)
    }
  }

  const updateResponseDelay = async (newDelay: number) => {
    if (!connection) return

    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/connections/${connection.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          response_delay_seconds: newDelay
        })
      })

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour')
      }

      // Recharger la connexion pour afficher la nouvelle valeur
      await loadConnection()
    } catch (err: any) {
      setError(err.message)
      throw err
    }
  }

  const updateBusinessHours = async (hours: any) => {
    if (!connection) return

    try {
      // Transformer le format frontend (open/close) vers backend (start/end)
      const transformedHours: Record<string, any> = {}
      for (const [day, schedule] of Object.entries(hours)) {
        const daySchedule = schedule as { enabled: boolean; open: string; close: string }
        transformedHours[day] = {
          enabled: daySchedule.enabled,
          start: daySchedule.open,
          end: daySchedule.close
        }
      }

      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/connections/${connection.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          business_hours: transformedHours
        })
      })

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour')
      }

      // Recharger la connexion pour afficher la nouvelle valeur
      await loadConnection()
    } catch (err: any) {
      setError(err.message)
      throw err
    }
  }

  const testMetaConnection = async () => {
    if (!connection || connection.connection_type !== 'meta_api') return
    setMetaTestLoading(true)
    setMetaTestResult(null)
    try {
      const token = getToken()
      const r = await fetch(`${getApiBase()}/whatsapp/connections/${connection.id}/test-meta`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : 'Erreur lors du test Meta'
        setMetaTestResult({ success: false, message: detail })
        return
      }
      setMetaTestResult({
        success: Boolean(data.success),
        message: data.message || (data.success ? 'OK' : 'Échec'),
      })
      await loadConnection()
    } catch (err: any) {
      setMetaTestResult({ success: false, message: err?.message || 'Erreur réseau' })
    } finally {
      setMetaTestLoading(false)
    }
  }

  const updateMessages = async (config: {
    welcome_message_enabled: boolean
    welcome_message: string | null
    closed_message_enabled: boolean
    closed_message: string
  }) => {
    if (!connection) return

    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/connections/${connection.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(config)
      })

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour')
      }

      // Recharger la connexion pour afficher la nouvelle valeur
      await loadConnection()
    } catch (err: any) {
      setError(err.message)
      throw err
    }
  }

  const getStatusBadge = (status: string, connectionType: 'baileys' | 'meta_api') => {
    switch (status) {
      case 'connected':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          Connecté
        </span>
      case 'connecting':
        if (connectionType === 'meta_api') {
          return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
            Initialisation Meta (pas de QR)
          </span>
        }
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
          <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
          En attente du scan QR
        </span>
      case 'error':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
          Erreur
        </span>
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400">
          <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
          Déconnecté
        </span>
    }
  }

  if (loading) {
    return (
      <div className="p-4 text-center text-sm text-gray-500">
        Chargement...
      </div>
    )
  }

  if (!connection) {
    // Pas de connexion = afficher le choix du type de connexion
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800/50 p-4">
          <div className="flex items-start gap-3">
            <div className="text-3xl">💬</div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                WhatsApp Business
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Connectez votre numéro WhatsApp pour permettre à cet agent de répondre automatiquement aux messages WhatsApp.
              </p>
              
              {/* Switch type de connexion */}
              <div className="mb-4 space-y-3">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Type de connexion</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setConnectionType('baileys')}
                    className={`p-3 rounded-lg border-2 text-left transition ${
                      connectionType === 'baileys'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-200 dark:border-zinc-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-900 dark:text-white">Baileys (QR Code)</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Gratuit, scan QR code</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setConnectionType('meta_api')}
                    className={`p-3 rounded-lg border-2 text-left transition ${
                      connectionType === 'meta_api'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-200 dark:border-zinc-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-900 dark:text-white">Meta API</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Officiel, stable</div>
                  </button>
                </div>
              </div>

              {/* Formulaire Meta API */}
              {connectionType === 'meta_api' && (
                <div className="space-y-4 mb-4 p-3 bg-white dark:bg-zinc-900 rounded-lg border border-gray-200 dark:border-zinc-700">
                  <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    L’API Cloud Meta exige des <strong className="text-gray-800 dark:text-gray-200">identifiants d’application</strong> (token + Phone
                    Number ID) <em>côté Totemium</em>, et une étape séparée <strong className="text-gray-800 dark:text-gray-200">dans le portail
                    développeur Meta</strong> pour recevoir les messages entrants : vous y indiquez l’URL de callback et le même jeton de vérification
                    que ci‑dessous.
                  </p>

                  <div className="rounded-lg border border-emerald-200/80 dark:border-emerald-900/50 bg-emerald-50/80 dark:bg-emerald-950/30 p-3 space-y-2">
                    <p className="text-xs font-semibold text-emerald-900 dark:text-emerald-200">
                      1 — À configurer dans Meta (Developers → votre app → WhatsApp → Configuration)
                    </p>
                    <div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">Callback URL (copier-coller)</span>
                      <div className="mt-1 flex flex-wrap gap-2 items-center">
                        <code className="text-[11px] sm:text-xs break-all rounded bg-white dark:bg-zinc-900 px-2 py-1 border border-emerald-200 dark:border-emerald-800 text-gray-900 dark:text-gray-100">
                          {typeof window !== 'undefined' ? getWhatsAppMetaWebhookCallbackUrl() : '/webhook/meta'}
                        </code>
                        <button
                          type="button"
                          onClick={copyWebhookUrl}
                          className="shrink-0 text-xs px-2 py-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-500"
                        >
                          Copier
                        </button>
                        {copyHint && <span className="text-xs text-emerald-700 dark:text-emerald-300">{copyHint}</span>}
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Jeton de vérification : utilisez <strong>exactement</strong> la même valeur que le champ « Verify token » ci‑dessous (elle doit aussi
                      correspondre à la variable serveur <code className="text-[11px]">META_WEBHOOK_VERIFY_TOKEN</code> si votre hébergeur en définit une).
                    </p>
                    <a
                      href={META_WEBHOOK_DOCS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block text-xs text-emerald-700 dark:text-emerald-400 underline"
                    >
                      Documentation Meta — Configurer les webhooks
                    </a>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Access Token (depuis Meta : jeton système ou utilisateur avec whatsapp_business_messaging, etc.)
                    </label>
                    <input
                      type="password"
                      autoComplete="off"
                      value={metaAccessToken}
                      onChange={(e) => setMetaAccessToken(e.target.value)}
                      placeholder="EAAx…"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone Number ID (identifiant du numéro WhatsApp Business dans l’API — pas le numéro de téléphone brut)
                    </label>
                    <input
                      type="text"
                      value={metaPhoneNumberId}
                      onChange={(e) => setMetaPhoneNumberId(e.target.value)}
                      placeholder="ex. 123456789012345"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Verify token (webhook Meta — identique dans le portail Meta et sur ce serveur)
                    </label>
                    <input
                      type="text"
                      value={metaWebhookVerifyToken}
                      onChange={(e) => setMetaWebhookVerifyToken(e.target.value)}
                      placeholder={DEFAULT_META_WEBHOOK_VERIFY}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-gray-900 dark:text-white font-mono text-xs"
                    />
                    <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                      Valeur par défaut Totemium : <code className="font-mono">{DEFAULT_META_WEBHOOK_VERIFY}</code>. Ne changez que si vous l’avez
                      modifiée sur le serveur (<code className="font-mono">META_WEBHOOK_VERIFY_TOKEN</code>).
                    </p>
                  </div>
                </div>
              )}

              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 mb-4">
                <li>✓ Réponses automatiques 24/7</li>
                <li>✓ Horaires d'ouverture configurables</li>
                <li>✓ Historique des conversations</li>
                <li>✓ Anti-ban avec délais variables</li>
              </ul>
              <button
                type="button"
                onClick={() => {
                  if (connectionType === 'meta_api' && (!metaAccessToken || !metaPhoneNumberId)) {
                    setError('Veuillez remplir Access Token et Phone Number ID')
                    return
                  }
                  createConnection(connectionType, {
                    metaAccessToken,
                    metaPhoneNumberId,
                    metaWebhookVerifyToken,
                  })
                }}
                disabled={creating}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? 'Activation...' : '💬 Activer WhatsApp'}
              </button>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}
      </div>
    )
  }

  // Connexion existe
  return (
    <div className="space-y-4">
      {/* Notification de succès */}
      {justConnected && (
        <div className="rounded-lg border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-900/20 p-4 flex items-start gap-3 animate-fadeIn">
          <svg className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">
              ✅ WhatsApp connecté avec succès !
            </h3>
            <p className="text-xs text-green-700 dark:text-green-300">
              Votre compte WhatsApp est maintenant lié à cet agent. Les messages entrants seront traités automatiquement.
            </p>
          </div>
        </div>
      )}

      {/* Statut et informations */}
      <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            État de la connexion
          </h3>
          {getStatusBadge(connection.status, connection.connection_type)}
        </div>

        {/* Bouton QR Code si status = connecting */}
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Mode actuel :{' '}
          <span className="font-medium text-gray-800 dark:text-gray-200">
            {connection.connection_type === 'meta_api' ? 'Meta API (officiel)' : 'Baileys (scan QR)'}
          </span>
        </p>

        {connection.connection_type === 'meta_api' && (
          <div className="rounded-lg border border-blue-200 dark:border-blue-900/50 bg-blue-50/90 dark:bg-blue-950/30 p-3 space-y-2 text-xs text-blue-900 dark:text-blue-100">
            <p className="font-semibold">Webhook Meta (messages entrants)</p>
            <p className="text-blue-800/90 dark:text-blue-200/90 leading-relaxed">
              Renseignez dans Meta Developers → WhatsApp → Configuration : l’URL de callback{' '}
              <code className="break-all font-mono text-[11px] px-1 rounded bg-white/80 dark:bg-zinc-900/80">
                {getWhatsAppMetaWebhookCallbackUrl()}
              </code>{' '}
              et le jeton de vérification{' '}
              <code className="font-mono text-[11px]">
                {connection.meta_webhook_verify_token || DEFAULT_META_WEBHOOK_VERIFY}
              </code>
              . Sans cette étape, Totemium peut valider le token d’accès mais ne recevra pas les messages utilisateur.
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              <button
                type="button"
                onClick={copyWebhookUrl}
                className="text-xs px-3 py-1 rounded-full bg-blue-600 text-white hover:bg-blue-500"
              >
                Copier l’URL du webhook
              </button>
              {copyHint && <span className="text-[11px] text-blue-700 dark:text-blue-300">{copyHint}</span>}
              <a
                href={META_WEBHOOK_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] underline text-blue-800 dark:text-blue-200"
              >
                Guide Meta (webhooks)
              </a>
            </div>
            <p className="text-[11px] text-blue-800/80 dark:text-blue-300/80">
              Vous n’avez pas besoin du WABA ID pour cette intégration standard : le Phone Number ID suffit pour l’API Cloud. Abonnez le champ « messages »
              sur le webhook dans Meta.
            </p>
          </div>
        )}

        {connection.connection_type === 'meta_api' && (
          <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/80 dark:bg-emerald-950/30 p-3 space-y-2">
            <p className="text-xs font-medium text-emerald-900 dark:text-emerald-200">
              Tester la connexion Meta (Graph API)
            </p>
            <p className="text-[11px] text-emerald-800/90 dark:text-emerald-300/90">
              Vérifie que le jeton et le Phone Number ID sont acceptés par Meta (équivalent d&apos;un e-mail de test SendGrid). Après succès, un statut bloqué peut être mis à jour.
            </p>
            <button
              type="button"
              onClick={testMetaConnection}
              disabled={metaTestLoading}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {metaTestLoading ? (
                <>
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Test en cours…
                </>
              ) : (
                <>Tester la connexion Meta</>
              )}
            </button>
            {metaTestResult && (
              <div
                className={`text-xs rounded-lg px-3 py-2 border ${
                  metaTestResult.success
                    ? 'bg-emerald-100/80 border-emerald-300 text-emerald-900 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-200'
                    : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950/40 dark:border-red-900 dark:text-red-300'
                }`}
              >
                {metaTestResult.message}
              </div>
            )}
          </div>
        )}

        {connection.status === 'connecting' && connection.connection_type === 'baileys' && (
          <button
            type="button"
            onClick={async () => {
              setQrFetchError(null)
              await loadConnection({ silent: true })
              setShowQR(true)
            }}
            className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
            {connection.qr_code ? 'Afficher le QR code' : 'Ouvrir la fenêtre de scan QR'}
          </button>
        )}

        {connection.phone_number && (
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Numéro</label>
            <div className="text-sm font-mono text-gray-900 dark:text-white">
              +{connection.phone_number}
            </div>
          </div>
        )}

        {connection.display_name && (
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Nom d'affichage</label>
            <div className="text-sm text-gray-900 dark:text-white">
              {connection.display_name}
            </div>
          </div>
        )}

        {connection.last_connected_at && (
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Dernière connexion</label>
            <div className="text-sm text-gray-900 dark:text-white">
              {new Date(connection.last_connected_at).toLocaleString('fr-FR')}
            </div>
          </div>
        )}

        {connection.disconnect_reason && (
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Raison de déconnexion</label>
            <div className="text-sm text-red-600 dark:text-red-400">
              {connection.disconnect_reason}
            </div>
          </div>
        )}

        {/* Connexion Baileys interrompue : libellé différent si jamais connecté vs reconnexion */}
        {(connection.status === 'disconnected' || connection.status === 'error') && (
          <button
            type="button"
            onClick={async () => {
              try {
                setError(null)
                const token = getToken()
                
                // Réinitialiser la connexion via l'API
                const response = await fetch(`${getApiBase()}/whatsapp/connections/${connection.id}/reconnect`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                })
                
                if (!response.ok) {
                  throw new Error('Erreur lors de la reconnexion')
                }

                const data = await response.json()
                setConnection(data)
                setQrFetchError(null)
                if (data.connection_type === 'baileys' && data.status === 'connecting') {
                  setShowQR(true)
                  ;[400, 1500, 4000].forEach((ms) =>
                    window.setTimeout(() => void loadConnection({ silent: true }), ms)
                  )
                } else {
                  await loadConnection({ silent: true })
                }
              } catch (err: any) {
                setError(err.message)
              }
            }}
            className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {connection.connection_type === 'baileys'
              ? connection.last_connected_at
                ? 'Reconnecter WhatsApp (nouveau QR)'
                : 'Obtenir le QR code et connecter'
              : 'Reconnecter WhatsApp'}
          </button>
        )}

        <button
          type="button"
          onClick={removeWhatsAppIntegration}
          className="w-full px-4 py-2 border border-red-300 dark:border-red-900 text-red-700 dark:text-red-400 text-sm font-medium rounded-lg transition hover:bg-red-50 dark:hover:bg-red-950/30"
        >
          Supprimer l&apos;intégration (changer Baileys / Meta)
        </button>
      </div>

      {/* Modal QR Code */}
      <WhatsAppQRModal
        isOpen={showQR}
        qrCode={connection.qr_code}
        fetchError={qrFetchError}
        onClose={() => {
          setShowQR(false)
          setQrFetchError(null)
        }}
      />

      {/* Éditeur délai de réponse */}
      <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-4">
        <ResponseDelayEditor
          initialDelay={connection.response_delay_seconds}
          connectionId={connection.id}
          onUpdate={updateResponseDelay}
        />
      </div>

      {/* Éditeur horaires d'ouverture */}
      <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-4">
        <BusinessHoursEditor
          initialHours={transformBusinessHoursFromBackend(connection.business_hours)}
          connectionId={connection.id}
          timezone={connection.timezone}
          onUpdate={updateBusinessHours}
        />
      </div>

      {/* Configuration messages automatiques */}
      <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-4">
        <WhatsAppMessagesConfig
          connectionId={connection.id}
          welcomeEnabled={connection.welcome_message_enabled}
          welcomeMessage={connection.welcome_message}
          closedEnabled={connection.closed_message_enabled}
          closedMessage={connection.closed_message || ''}
          onUpdate={updateMessages}
        />
      </div>

      {/* Configuration Human Takeover */}
      <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
              Intervention humaine
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Désactiver automatiquement l'agent quand un humain prend le relais dans une conversation
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer ml-4">
            <input
              type="checkbox"
              checked={connection.human_takeover_enabled}
              onChange={async (e) => {
                try {
                  const token = getToken()
                  const response = await fetch(`${getApiBase()}/whatsapp/connections/${connection.id}`, {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                      human_takeover_enabled: e.target.checked
                    })
                  })
                  
                  if (!response.ok) {
                    throw new Error('Erreur lors de la mise à jour')
                  }
                  
                  await loadConnection()
                } catch (err: any) {
                  setError(err.message)
                }
              }}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 dark:peer-focus:ring-emerald-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-emerald-600"></div>
          </label>
        </div>
        
        {connection.human_takeover_enabled && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900 rounded-lg">
            <p className="text-xs text-blue-700 dark:text-blue-400">
              <strong>💡 Comment ça marche ?</strong><br/>
              Une fois qu'un humain prend le contrôle d'une conversation (via l'API ou une interface dédiée), 
              l'agent cessera automatiquement de répondre à cette conversation jusqu'à ce que le contrôle soit relâché.
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}
    </div>
  )
}
